import React from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, Phone, MapPin, Scale, FileText, Users } from 'lucide-react';
import { Link } from 'react-router-dom';

const FooterEnhanced = () => {
  const { i18n } = useTranslation();
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-gradient-to-br from-wazin-navy-dark via-wazin-navy to-wazin-navy-dark text-white border-t-4 border-wazin-gold">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          
          {/* عن وازن - About Wazin */}
          <div className="col-span-1">
            <div className="mb-6">
              <img 
                src="/images/wazin-logo.png" 
                alt="Wazin Logo" 
                className="h-16 md:h-20 w-auto object-contain"
                onError={(e) => {
                  e.target.src = '/images/wazin-icon.png';
                }}
              />
            </div>
            <h4 className="text-xl font-bold mb-4 text-wazin-gold">
              {i18n.language === 'ar' ? 'عن وازن' : 'About Wazin'}
            </h4>
            <p className="text-gray-300 text-sm leading-relaxed">
              شركة وازن للمحاماة والاستشارات القانونية
            </p>
            <p className="text-gray-300 text-sm mt-2">
              جدة – حي الرويس
            </p>
          </div>

          {/* خدماتنا - Our Services */}
          <div>
            <h4 className="text-xl font-bold mb-4 text-wazin-gold">
              {i18n.language === 'ar' ? 'خدماتنا' : 'Our Services'}
            </h4>
            <ul className="space-y-3">
              <li>
                <Link 
                  to="/services" 
                  className="text-gray-300 hover:text-wazin-gold transition-colors text-sm flex items-center gap-2 group"
                >
                  <Scale className="w-4 h-4 text-wazin-gold group-hover:scale-110 transition-transform" />
                  {i18n.language === 'ar' ? 'جميع الخدمات القانونية' : 'All Legal Services'}
                </Link>
              </li>
              <li>
                <Link 
                  to="/services" 
                  className="text-gray-300 hover:text-wazin-gold transition-colors text-sm flex items-center gap-2 group"
                >
                  <FileText className="w-4 h-4 text-wazin-gold group-hover:scale-110 transition-transform" />
                  {i18n.language === 'ar' ? 'القضايا التجارية' : 'Commercial Cases'}
                </Link>
              </li>
              <li>
                <Link 
                  to="/services" 
                  className="text-gray-300 hover:text-wazin-gold transition-colors text-sm flex items-center gap-2 group"
                >
                  <Users className="w-4 h-4 text-wazin-gold group-hover:scale-110 transition-transform" />
                  {i18n.language === 'ar' ? 'قضايا الأحوال الشخصية' : 'Family Law'}
                </Link>
              </li>
              <li>
                <Link 
                  to="/services" 
                  className="text-gray-300 hover:text-wazin-gold transition-colors text-sm flex items-center gap-2 group"
                >
                  <FileText className="w-4 h-4 text-wazin-gold group-hover:scale-110 transition-transform" />
                  {i18n.language === 'ar' ? 'صياغة العقود' : 'Contract Drafting'}
                </Link>
              </li>
            </ul>
          </div>

          {/* روابط مهمة - Important Links */}
          <div>
            <h4 className="text-xl font-bold mb-4 text-wazin-gold">
              {i18n.language === 'ar' ? 'روابط مهمة' : 'Important Links'}
            </h4>
            <ul className="space-y-3">
              <li>
                <Link 
                  to="/" 
                  className="text-gray-300 hover:text-wazin-gold transition-colors text-sm flex items-center gap-2"
                >
                  <span className="text-wazin-gold">›</span>
                  {i18n.language === 'ar' ? 'الرئيسية' : 'Home'}
                </Link>
              </li>
              <li>
                <Link 
                  to="/#testimonials" 
                  className="text-gray-300 hover:text-wazin-gold transition-colors text-sm flex items-center gap-2"
                >
                  <span className="text-wazin-gold">›</span>
                  {i18n.language === 'ar' ? 'آراء العملاء' : 'Testimonials'}
                </Link>
              </li>
              <li>
                <Link 
                  to="/privacy-policy" 
                  className="text-gray-300 hover:text-wazin-gold transition-colors text-sm flex items-center gap-2"
                >
                  <span className="text-wazin-gold">›</span>
                  {i18n.language === 'ar' ? 'سياسة الخصوصية' : 'Privacy Policy'}
                </Link>
              </li>
              <li>
                <Link 
                  to="/terms-conditions" 
                  className="text-gray-300 hover:text-wazin-gold transition-colors text-sm flex items-center gap-2"
                >
                  <span className="text-wazin-gold">›</span>
                  {i18n.language === 'ar' ? 'الشروط والأحكام' : 'Terms & Conditions'}
                </Link>
              </li>
            </ul>
          </div>

          {/* تواصل معنا - Contact Us */}
          <div>
            <h4 className="text-xl font-bold mb-4 text-wazin-gold">
              {i18n.language === 'ar' ? 'تواصل معنا' : 'Contact Us'}
            </h4>
            <ul className="space-y-4">
              {/* Email */}
              <li className="flex items-start gap-3 text-gray-300 text-sm">
                <Mail className="w-5 h-5 mt-0.5 flex-shrink-0 text-wazin-gold" />
                <a 
                  href="mailto:info@wazin.law" 
                  className="hover:text-wazin-gold transition-colors break-all"
                >
                  info@wazin.law
                </a>
              </li>
              
              {/* Phone */}
              <li className="flex items-start gap-3 text-gray-300 text-sm">
                <Phone className="w-5 h-5 mt-0.5 flex-shrink-0 text-wazin-gold" />
                <a 
                  href="tel:0122226665" 
                  dir="ltr" 
                  className="hover:text-wazin-gold transition-colors"
                >
                  0122226665
                </a>
              </li>
              
              {/* Address */}
              <li className="flex items-start gap-3 text-gray-300 text-sm">
                <MapPin className="w-5 h-5 mt-0.5 flex-shrink-0 text-wazin-gold" />
                <div>
                  <div className="font-semibold text-wazin-gold mb-1">
                    {i18n.language === 'ar' ? 'المقر الرئيسي:' : 'Main Office:'}
                  </div>
                  <div>
                    {i18n.language === 'ar'
                      ? 'جدة – حي الرويس – المملكة العربية السعودية'
                      : 'Jeddah – Al Rawais District – Saudi Arabia'
                    }
                  </div>
                  <div className="text-xs mt-2 text-gray-400">
                    {i18n.language === 'ar'
                      ? 'نخدم عملاءنا في جميع مناطق المملكة'
                      : 'We serve clients across all regions of the Kingdom'
                    }
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-wazin-gold/30 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-400 text-sm text-center md:text-right">
            &copy; {currentYear} <span className="text-wazin-gold font-bold">
              {i18n.language === 'ar' ? 'شركة وازن للمحاماة' : 'Wazin Law Firm'}
            </span>
            {' '}{i18n.language === 'ar' ? '• جميع الحقوق محفوظة' : '• All Rights Reserved'}
          </p>
          
          <div className="flex items-center gap-4">
            <Link 
              to="/privacy-policy" 
              className="text-gray-400 hover:text-wazin-gold text-xs transition-colors"
            >
              {i18n.language === 'ar' ? 'سياسة الخصوصية' : 'Privacy'}
            </Link>
            <span className="text-gray-600">|</span>
            <Link 
              to="/terms-conditions" 
              className="text-gray-400 hover:text-wazin-gold text-xs transition-colors"
            >
              {i18n.language === 'ar' ? 'الشروط والأحكام' : 'Terms'}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default FooterEnhanced;
